import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import io from 'socket.io-client';
import { getCookie, removeCookie } from '../utils/cookie-service';
import { SOCKET_EVENT_NAME } from '../constants/constants';
import { logOut } from '../store/auth/actions';
import routes from '../constants/routes';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

export const SocketProviderComponent = ({ children, userId, isAuthenticated, logOut }) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const reconnectTimeoutRef = useRef(null);
  const eventHandlersRef = useRef(new Map());
  const navigate = useNavigate();

  let apiUrl = process.env.REACT_APP_BASE_API_URL;
  if (process.env.NODE_ENV === 'development') {
    apiUrl = `http://${process.env.REACT_APP_ALLOWED_HOST || '192.168.102.78'}:3003`;
  }

  // const apiUrl = 'http://192.168.102.78:3003';

  // Validate environment variable
  if (!apiUrl) {
    console.error('REACT_APP_BASE_API_URL is not defined');
    throw new Error('Socket API URL is not configured');
  }

  const handleSetUnreadCount = useCallback((count) => {
    if (typeof count === 'number' && count >= 0) {
      setTotalUnreadCount(count);
    } else {
      console.warn('Invalid unread count received:', count);
    }
  }, []);

  const handleLogout = useCallback(() => {
    logOut();
    removeCookie('redirectPath');
    navigate(routes.login);
  }, [logOut, navigate]);

  const cleanupSocket = useCallback(() => {
    if (socketRef.current) {
      // Clear all event listeners
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Clear any pending reconnect timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Clear event handlers map
    eventHandlersRef.current.clear();

    setIsConnected(false);
  }, []);

  const initializeSocket = useCallback(() => {
    if (!userId || !isAuthenticated) {
      cleanupSocket();
      return;
    }

    // Don't create multiple instances
    if (socketRef.current) {
      cleanupSocket();
    }

    const token = getCookie('veriipro-chat-token');
    if (!token) {
      console.error('Authentication token not found');
      return;
    }

    try {
      socketRef.current = io(apiUrl, {
        autoConnect: false,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        timeout: 20000,
        forceNew: true, // Prevent connection reuse
        query: { token },
        transports: ['websocket', 'polling']
      });

      const socket = socketRef.current;

      // Connection event handlers
      socket.on(SOCKET_EVENT_NAME.CONNECT, () => {
        console.log('Socket connected successfully');
        setIsConnected(true);
      });

      socket.on(SOCKET_EVENT_NAME.DISCONNECT, (reason) => {
        console.log('Socket disconnected:', reason);
        setIsConnected(false);

        // Handle different disconnect reasons
        if (reason === 'io server disconnect') {
          // Server disconnected, need manual reconnection
          reconnectTimeoutRef.current = setTimeout(() => {
            if (socketRef.current && !socketRef.current.connected) {
              socket.connect();
            }
          }, 2000);
        }
      });

      socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        setIsConnected(false);
      });

      socket.on('reconnect', (attemptNumber) => {
        console.log('Socket reconnected after', attemptNumber, 'attempts');
        setIsConnected(true);
      });

      socket.on('reconnect_error', (error) => {
        console.error('Socket reconnection error:', error);
      });

      socket.on('reconnect_failed', () => {
        console.error('Socket reconnection failed after maximum attempts');
      });

      // Business logic event handlers
      socket.on(SOCKET_EVENT_NAME.UNREAD_COUNT, handleSetUnreadCount);
      socket.on(SOCKET_EVENT_NAME.LOGOUT_USER, handleLogout);

      // Connect only if online
      if (navigator.onLine) {
        socket.connect();
      } else {
        console.warn('User is offline, socket connection deferred');
      }
    } catch (error) {
      console.error('Error initializing socket:', error);
    }
  }, [userId, isAuthenticated, apiUrl, handleSetUnreadCount, cleanupSocket]);

  useEffect(() => {
    initializeSocket();

    const handleOnline = () => {
      console.log('📶 Network online - attempting socket connection');
      if (socketRef.current && !socketRef.current.connected) {
        socketRef.current.connect();
      } else if (!socketRef.current && userId && isAuthenticated) {
        // Reinitialize socket if it was cleaned up
        initializeSocket();
      }
    };

    const handleOffline = () => {
      console.log('📵 Network offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      cleanupSocket();
    };
  }, [initializeSocket, cleanupSocket, userId, isAuthenticated]);

  const emit = useCallback((event, data, callback) => {
    if (!socketRef.current?.connected) {
      console.warn('Cannot emit event: socket not connected');
      if (callback) callback({ error: 'Socket not connected' });
      return false;
    }
    try {
      socketRef.current.emit(event, data, callback);
      return true;
    } catch (error) {
      console.error('Error emitting event:', error);
      if (callback) callback({ error: error.message });
      return false;
    }
  }, []);

  const on = useCallback((event, callback) => {
    if (!socketRef.current) {
      console.warn('Cannot add listener: socket not initialized');
      return () => {};
    }

    if (typeof callback !== 'function') {
      console.error('Event callback must be a function');
      return () => {};
    }
    try {
      socketRef.current.on(event, callback);

      // Store reference for cleanup
      if (!eventHandlersRef.current.has(event)) {
        eventHandlersRef.current.set(event, new Set());
      }
      eventHandlersRef.current.get(event).add(callback);

      // Return cleanup function
      return () => {
        if (socketRef.current) {
          socketRef.current.off(event, callback);
        }
        if (eventHandlersRef.current.has(event)) {
          eventHandlersRef.current.get(event).delete(callback);
        }
      };
    } catch (error) {
      console.error('Error adding event listener:', error);
      return () => {};
    }
  }, []);

  const off = useCallback((event, callback) => {
    if (!socketRef.current) {
      return;
    }

    try {
      if (callback) {
        socketRef.current.off(event, callback);
        if (eventHandlersRef.current.has(event)) {
          eventHandlersRef.current.get(event).delete(callback);
        }
      } else {
        socketRef.current.off(event);
        if (eventHandlersRef.current.has(event)) {
          eventHandlersRef.current.delete(event);
        }
      }
    } catch (error) {
      console.error('Error removing event listener:', error);
    }
  }, []);

  const contextValue = {
    emit,
    on,
    off,
    isConnected,
    totalUnreadCount,
    reconnect: () => {
      if (socketRef.current) {
        socketRef.current.connect();
      } else {
        initializeSocket();
      }
    }
  };

  return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
};
export const SocketProvider = connect(null, { logOut })(SocketProviderComponent);
